/**
 * Generiek periode-filter voor het payroll-/uren-overzicht + export
 * (batch-9-vervolg V5, PO #10).
 *
 * Zet een periode-keuze (vrij / week / maand / kwartaal / jaar) om naar een
 * concrete `[van, totEnMet]`-datumrange (yyyy-mm-dd) plus een leesbaar label.
 * De bestaande free-range werkuren-export (`van`/`totEnMet`) draait er bovenop -
 * geen tweede rekenkern. Week = ISO (maandag-zondag); maand/kwartaal/jaar in
 * Europe/Amsterdam-kalendertermen (de DB-grenzen blijven UTC bij de export).
 *
 * Pure functie, zero deps (CLAUDE.md: geen date-fns/dayjs).
 */

import { mondayOfIsoWeek } from './iso-week'

export const PAYROLL_PERIODE_TYPES = ['VRIJ', 'WEEK', 'MAAND', 'KWARTAAL', 'JAAR'] as const
export type PayrollPeriodeType = (typeof PAYROLL_PERIODE_TYPES)[number]

export interface PayrollPeriodeRange {
  /** Inclusieve startdatum, yyyy-mm-dd. */
  van: string
  /** Inclusieve einddatum, yyyy-mm-dd. */
  totEnMet: string
  /** Leesbaar label voor de UI/export-kop (NL). */
  label: string
}

const MAAND_NAMEN = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
]

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** Laatste dag van een maand (1-12) als UTC-datum. */
function laatsteDagVanMaand(jaar: number, maand1: number): Date {
  // Dag 0 van de volgende maand = laatste dag van deze maand.
  return new Date(Date.UTC(jaar, maand1, 0))
}

export interface PayrollPeriodeInput {
  type: PayrollPeriodeType
  jaar: number
  /** Bij MAAND: 1-12. */
  maand?: number | null
  /** Bij WEEK: ISO-weeknummer 1-53. */
  week?: number | null
  /** Bij KWARTAAL: 1-4. */
  kwartaal?: number | null
  /** Bij VRIJ: expliciete grenzen (yyyy-mm-dd). */
  van?: string | null
  totEnMet?: string | null
}

/**
 * Resolve de gekozen periode naar een concrete `[van, totEnMet]`-range + label.
 * Gooit een `Error` met een nette boodschap bij een onvolledige/ongeldige keuze
 * (de caller mapt dit naar een 400/validatiefout).
 */
export function resolvePayrollPeriode(input: PayrollPeriodeInput): PayrollPeriodeRange {
  const { type, jaar } = input

  if (type === 'VRIJ') {
    if (!input.van || !input.totEnMet) {
      throw new Error('Bij een vrije periode zijn een begin- en einddatum verplicht.')
    }
    if (input.van > input.totEnMet) {
      throw new Error('De begindatum mag niet na de einddatum liggen.')
    }
    return {
      van: input.van,
      totEnMet: input.totEnMet,
      label: `${input.van} t/m ${input.totEnMet}`,
    }
  }

  if (type === 'WEEK') {
    const week = input.week ?? null
    if (week == null || week < 1 || week > 53) {
      throw new Error('Kies een geldig weeknummer (1-53).')
    }
    const maandag = mondayOfIsoWeek(week, jaar)
    const zondag = new Date(
      Date.UTC(maandag.getUTCFullYear(), maandag.getUTCMonth(), maandag.getUTCDate() + 6),
    )
    return {
      van: ymd(maandag),
      totEnMet: ymd(zondag),
      label: `Week ${week} ${jaar}`,
    }
  }

  if (type === 'MAAND') {
    const maand = input.maand ?? null
    if (maand == null || maand < 1 || maand > 12) {
      throw new Error('Kies een geldige maand (1-12).')
    }
    const eerste = new Date(Date.UTC(jaar, maand - 1, 1))
    const laatste = laatsteDagVanMaand(jaar, maand)
    return {
      van: ymd(eerste),
      totEnMet: ymd(laatste),
      label: `${MAAND_NAMEN[maand - 1]} ${jaar}`,
    }
  }

  if (type === 'KWARTAAL') {
    const kw = input.kwartaal ?? null
    if (kw == null || kw < 1 || kw > 4) {
      throw new Error('Kies een geldig kwartaal (1-4).')
    }
    const startMaand = (kw - 1) * 3 // 0-based maand
    const eerste = new Date(Date.UTC(jaar, startMaand, 1))
    const laatste = laatsteDagVanMaand(jaar, startMaand + 3)
    return {
      van: ymd(eerste),
      totEnMet: ymd(laatste),
      label: `Q${kw} ${jaar}`,
    }
  }

  // JAAR
  const eerste = new Date(Date.UTC(jaar, 0, 1))
  const laatste = new Date(Date.UTC(jaar, 11, 31))
  return {
    van: ymd(eerste),
    totEnMet: ymd(laatste),
    label: `Jaar ${jaar}`,
  }
}
